import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatCLP } from "@/lib/api";
import { REGIONES, TASK_TYPES } from "@/lib/constants";
import { BadgeCheck, MapPin, Clock, Plus, Filter } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const STATUS_LABEL = {
  open: { l: "Abierta", cls: "badge-cyan" },
  assigned: { l: "Asignada · Pago Protegido pendiente", cls: "badge-pending" },
  in_progress: { l: "En curso", cls: "badge-cyan" },
  awaiting_approval: { l: "Acta · esperando", cls: "badge-pending" },
  completed: { l: "Completada", cls: "badge-verified" },
  cancelled: { l: "Cancelada", cls: "" },
};

const TABS = [
  { key: "assigned", label: "Asignadas a mí", params: { mine: "assigned" } },
  { key: "owned", label: "Publiqué yo", params: { mine: "owned" } },
  { key: "open", label: "Directorio abierto", params: { status: "open" } },
];

export default function Delegations() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const initialTab = params.get("mine") === "assigned" ? "assigned"
    : params.get("mine") === "owned" ? "owned"
    : "open";
  const [tab, setTab] = useState(initialTab);
  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState({
    region: params.get("region") || "",
    task_type: params.get("task_type") || "",
  });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (activeTab, activeFilters) => {
    setLoading(true);
    try {
      const tabCfg = TABS.find(t => t.key === activeTab) || TABS[0];
      const q = { ...tabCfg.params };
      if (activeFilters.region) q.region = activeFilters.region;
      if (activeFilters.task_type) q.task_type = activeFilters.task_type;
      const { data } = await api.get("/delegations", { params: q });
      setItems(data.items);
      setParams({ ...q });
    } finally { setLoading(false); }
  }, [setParams]);

  useEffect(() => { load(tab, filters); /* eslint-disable-next-line */ }, [tab]);

  const applyFilters = () => load(tab, filters);

  return (
    <div className="container-app py-12" data-testid="delegations-page">
      <div className="flex items-end justify-between gap-6 flex-wrap">
        <div>
          <div className="label-eyebrow">Directorio</div>
          <h1 className="serif text-5xl mt-3">Delegaciones</h1>
          <p className="mt-2 text-muted-foreground">Audiencias, comparendos y gestiones entre colegas verificados.</p>
        </div>
        {user?.role === "lawyer" && (
          <Link to="/app/delegaciones/nueva" className="btn-primary" data-testid="new-delegation-btn"><Plus size={16} className="mr-1.5"/> Publicar delegación</Link>
        )}
      </div>

      <div className="mt-8 flex items-center gap-2 flex-wrap" data-testid="del-tabs">
        {TABS.map((t) => (
          <button key={t.key} onClick={()=>setTab(t.key)}
            className={`pill ${tab === t.key ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] border-transparent" : ""}`}
            data-testid={`del-tab-${t.key}`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-5 panel p-4 grid sm:grid-cols-3 gap-3">
        <select className="field" value={filters.region} onChange={(e)=>setFilters({...filters, region:e.target.value})} data-testid="filter-region">
          <option value="">Todas las regiones</option>
          {REGIONES.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <select className="field" value={filters.task_type} onChange={(e)=>setFilters({...filters, task_type:e.target.value})} data-testid="filter-task">
          <option value="">Todo tipo</option>
          {TASK_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <button onClick={applyFilters} className="btn-ghost" data-testid="apply-filters"><Filter size={14} className="mr-1.5"/>Aplicar filtros</button>
      </div>

      <ul className="mt-6 space-y-3" data-testid="delegations-list">
        {loading && <li className="panel p-10 text-center text-muted-foreground">Cargando…</li>}
        {!loading && items.length === 0 && (
          <li className="panel p-10 text-center text-muted-foreground" data-testid="del-empty">
            {tab === "assigned" && "Aún no tienes delegaciones asignadas. Postula en el Directorio abierto."}
            {tab === "owned" && (user?.role === "lawyer"
              ? <>Aún no has publicado. <Link to="/app/delegaciones/nueva" className="link-underline text-foreground">Crear la primera</Link></>
              : "Solo los abogados pueden publicar delegaciones.")}
            {tab === "open" && "No hay delegaciones abiertas con esos filtros."}
          </li>
        )}
        {items.map((d) => {
          const s = STATUS_LABEL[d.status] || { l: d.status, cls: "" };
          return (
            <li key={d.id} className="panel p-6 hover:border-foreground/40 transition-colors" data-testid={`delegation-card-${d.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Link to={`/app/delegaciones/${d.id}`} className="serif text-2xl link-underline">{d.title}</Link>
                    <span className="pill capitalize text-xs">{d.task_type}</span>
                    <span className={`pill ${s.cls}`}>{s.l}</span>
                    {d.owner_verified && <span className="pill badge-verified"><BadgeCheck size={12}/>Verificado</span>}
                  </div>
                  <div className="mt-2 text-sm text-muted-foreground flex flex-wrap gap-x-5 gap-y-1">
                    <span className="inline-flex items-center gap-1.5"><MapPin size={13}/>{d.comuna}, {d.region}</span>
                    <span className="inline-flex items-center gap-1.5"><Clock size={13}/>{d.date} · {d.time}</span>
                    <span className="truncate">{d.court}{d.case_number ? ` · ${d.case_number}` : ""}</span>
                    {tab === "assigned" && <span>Titular: {d.owner_name}</span>}
                    {tab === "owned" && d.assigned_to_name && <span>Asignada a: {d.assigned_to_name}</span>}
                  </div>
                  {d.notes && <p className="mt-3 text-sm text-foreground/80 line-clamp-2">{d.notes}</p>}
                </div>
                <div className="text-right">
                  <div className="serif text-3xl">{formatCLP(d.agreed_fee_clp || d.fee_clp)}</div>
                  {d.status === "open" && <div className="label-eyebrow mt-1">{d.bids_count || 0} postulaciones</div>}
                  {d.payment_status === "in_escrow" && <span className="pill badge-cyan mt-2 inline-flex">Pago Protegido activo</span>}
                  {d.payment_status === "released" && <span className="pill badge-verified mt-2 inline-flex">Liberado</span>}
                  <Link to={`/app/delegaciones/${d.id}`} className="btn-ghost mt-3 text-sm py-2" data-testid={`view-delegation-${d.id}`}>Ver detalle</Link>
                  {tab === "owned" && d.status === "open" && !["in_escrow","released"].includes(d.payment_status) && (
                    <Link to={`/app/delegaciones/${d.id}/editar`} className="btn-ghost mt-2 text-sm py-2 inline-flex items-center gap-1 justify-center w-full" data-testid={`edit-delegation-${d.id}`}>
                      Editar audiencia
                    </Link>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
